import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";
import { TPlan } from "../types";

type PlanListParams = { page?: number; limit?: number; features?: string };
type PlanSaveCallbacks = { onSuccess?: (res: any) => void; onError?: (err: any) => void };

export const usePlanList = (qData: PlanListParams = { limit: 100, features: "1" }) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.PLAN, qData],
    queryFn: () => client.get(API_ENDPOINTS.PLAN, { params: qData }),
    select: (res: any) => res.data.data as TPlan[],
    refetchOnWindowFocus: false,
  });
};

export const usePlanDetailForEdit = (id: number | null) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.PLAN, id, "forEdit"],
    queryFn: () =>
      id
        ? client.get(API_ENDPOINTS.PLAN + id)
        : Promise.resolve({ data: { data: { is_active: true } } }),
    select: (res: any) => res.data.data as TPlan,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const usePlanSave = ({ onSuccess, onError }: PlanSaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => {
      if (data.id) return client.put(API_ENDPOINTS.PLAN + data.id, data);
      return client.post(API_ENDPOINTS.PLAN, data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.PLAN] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const usePlanDelete = ({ onSuccess, onError }: { onSuccess?: () => void; onError?: () => void } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.delete(API_ENDPOINTS.PLAN + id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.PLAN] });
      onSuccess?.();
    },
    onError: () => onError?.(),
  });
};
